import type {
  ChangeSetId,
  MonthlyReportId,
  RecommendationId,
  SeoAuditRunId,
  SiteId,
} from "./ids.js";
import type {
  EvidenceConfidence,
  MeasurementVerdict,
  RecommendationType,
  SeoAuditPurpose,
} from "./continuous-improvement.js";
import type { MonthlyEnquirySummary } from "./enquiries.js";
import type { MonthlySearchSummary } from "./search-console.js";

/**
 * The monthly "what was done for the website" report.
 *
 * It is a frozen statement about one past calendar month, assembled only from
 * records that already exist: published and rolled-back change sets, the
 * 7/14/28-day measurements, and dated audit scores. Nothing is estimated and
 * nothing is inferred. What the report cannot know is listed in `limitations`
 * rather than left out.
 */
export const MONTHLY_REPORT_VERSION = "siteagain.monthly_report.v1" as const;

/**
 * How long a draft waits for an operator before it is approved automatically. One number for the
 * worker that applies it and the screen that tells an operator about it.
 */
export const MONTHLY_REPORT_REVIEW_WINDOW_HOURS = 48;

export type MonthlyReportLocale = "en" | "fr" | "es";

/**
 * draft: generated, waiting for an operator to review it.
 * approved: visible to the customer (reviewed, or auto-approved after the review window).
 * suppressed: withheld by an operator; never shown or emailed.
 */
export type MonthlyReportStatus = "draft" | "approved" | "suppressed";
export type MonthlyReportEmailStatus = "pending" | "sent" | "skipped" | "failed";

/** Things a monthly report does not know. Codes, so each surface can say them in its own language. */
export type MonthlyReportLimitation =
  | "no_traffic_data"
  | "observational_only"
  | "summaries_in_original_language"
  /** Google's numbers did not reach the end of the month when the report was written. */
  | "search_data_incomplete"
  /** The site's contact form is not connected to SiteAgain, so the report cannot say how many people got in touch. */
  | "no_enquiry_data";

export interface MonthlyReportChange {
  changeSetId: ChangeSetId;
  /** The plan's own summary, shown exactly as written and in the language it was written. */
  summary: string;
  origin: "recommendation" | "request";
  recommendationId: RecommendationId | null;
  recommendationType: RecommendationType | null;
  pages: string[];
  deployedAt: string | null;
  rolledBackAt: string | null;
  /** Up to three evidence statements the recommendation was raised on. */
  evidence: string[];
}

export interface MonthlyReportNotShipped {
  changeSetId: ChangeSetId;
  summary: string;
  outcome: "rejected" | "failed";
  at: string;
}

export interface MonthlyReportMeasurement {
  recommendationId: RecommendationId;
  title: string;
  windowDays: 7 | 14 | 28;
  verdict: MeasurementVerdict;
  confidence: EvidenceConfidence;
  measuredAt: string;
  /** The measurement's own non-causality statement, verbatim. */
  caveat: string;
}

export interface MonthlyReportWaitingChange {
  changeSetId: ChangeSetId;
  summary: string;
  since: string;
}

export interface MonthlyReportScorePoint {
  auditRunId: SeoAuditRunId;
  purpose: SeoAuditPurpose;
  capturedAt: string;
  score: number | null;
  grade: string | null;
}

export interface MonthlyReportV1 {
  version: typeof MONTHLY_REPORT_VERSION;
  siteId: SiteId;
  siteName: string;
  domain: string | null;
  period: { month: string; start: string; end: string };
  generatedAt: string;
  locale: MonthlyReportLocale;
  /** Nothing was published, rolled back, measured, or waiting. Stored and shown, never emailed. */
  quiet: boolean;
  totals: {
    shipped: number;
    rolledBack: number;
    notShipped: number;
    measured: number;
    measuredImproved: number;
  };
  shipped: MonthlyReportChange[];
  rolledBack: MonthlyReportChange[];
  notShipped: MonthlyReportNotShipped[];
  measurements: MonthlyReportMeasurement[];
  waiting: {
    awaitingApproval: MonthlyReportWaitingChange[];
    openRecommendations: number;
    expiredRecommendations: number;
  };
  score: {
    /** Completed audits in the month, oldest first. */
    points: MonthlyReportScorePoint[];
    /** The last audit before the month began, when there was one. */
    before: MonthlyReportScorePoint | null;
    /** The last audit inside the month, when there was one. */
    end: MonthlyReportScorePoint | null;
    /** `end - before` when both exist and are scored; otherwise null. */
    delta: number | null;
  };
  /**
   * What Search Console shows for the month, when the site is connected and has numbers for it. Absent
   * otherwise, in which case `limitations` says the report does not cover traffic.
   */
  search?: MonthlySearchSummary;
  /**
   * How many people got in touch through the site's form, when the form is connected and was listening
   * during the month. Absent otherwise, in which case `limitations` says the report does not cover enquiries.
   */
  enquiries?: MonthlyEnquirySummary;
  limitations: MonthlyReportLimitation[];
}

export interface MonthlyReportRecord {
  id: MonthlyReportId;
  siteId: SiteId;
  month: string;
  periodStart: string;
  periodEnd: string;
  locale: MonthlyReportLocale;
  status: MonthlyReportStatus;
  quiet: boolean;
  report: MonthlyReportV1;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  emailStatus: MonthlyReportEmailStatus;
  emailDetail: string | null;
  emailedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What a customer sees of a report: approved reports only, with none of the review or delivery trail. */
export interface CustomerMonthlyReport {
  id: MonthlyReportId;
  siteId: SiteId;
  month: string;
  locale: MonthlyReportLocale;
  quiet: boolean;
  createdAt: string;
  report: MonthlyReportV1;
}
